"use client";

import { useEffect, useState, useCallback } from "react";
import { humanizeStatus } from "@/app/_module/utils/humanizeStatus";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/app/_module/components/ui/table";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/app/_module/components/ui/dialog";
import { Label } from "@/app/_module/components/ui/label";
import {
  Clock,
  CheckCircle,
  AlertTriangle,
  User,
  Calendar,
  Search,
  Filter,
  Plus,
  Settings,
  Mail,
  CreditCard,
  Ban,
  RefreshCw,
  Eye,
  Building,
  Phone,
  Hash
} from "lucide-react";
import { trialStatusService } from "@/app/_module/services/trialStatusService";
import { trialManagementService, type TrialUser, type TrialStats } from "@/app/_module/services/trialManagementService";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { useToast } from "@/app/_module/hooks/use-toast";

// Real API service integration - no mock data

interface TrialManagementProps {
  onUserSelect?: (user: TrialUser) => void;
}

export default function TrialManagement({ onUserSelect }: TrialManagementProps) {
  const [users, setUsers] = useState<TrialUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<TrialUser | null>(null);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showSendReminderDialog, setShowSendReminderDialog] = useState(false);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [adminNotes, setAdminNotes] = useState("");
  const [suspendReason, setSuspendReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const fetchTrialUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trialManagementService.getTrialUsers({
        status: statusFilter !== "all" ? statusFilter as any : undefined,
        search: searchTerm || undefined,
        page: 1,
        limit: 50
      });
      // Handle both direct array and nested response formats
      const usersData = Array.isArray(response) ? response : (response?.users || []);
      setUsers(usersData);
    } catch (err) {
      console.error('Trial users fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trial users');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchTrialUsers();
  }, [fetchTrialUsers]);

  const handleExtendTrial = async () => {
    if (!selectedUser) return;

    try {
      setIsProcessing(true);
      await trialManagementService.extendTrial(selectedUser.id, extendDays, adminNotes || undefined);
      toast({
        title: "Trial Extended",
        description: `Trial extended by ${extendDays} days for ${selectedUser.name}`,
      });
      setShowExtendDialog(false);
      setExtendDays(7);
      setAdminNotes("");
      fetchTrialUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to extend trial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendReminder = async () => {
    if (!selectedUser) return;

    try {
      setIsProcessing(true);
      await trialManagementService.sendTrialExpirationReminder(
        selectedUser.id,
        selectedUser.daysRemaining
      );
      toast({
        title: "Reminder Sent",
        description: `Trial expiration reminder sent to ${selectedUser.email}`,
      });
      setShowSendReminderDialog(false);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to send reminder. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSuspendUser = async () => {
    if (!selectedUser || !suspendReason) return;

    try {
      setIsProcessing(true);
      await trialManagementService.suspendTrialUser(selectedUser.id, suspendReason, adminNotes || undefined);
      toast({
        title: "User Suspended",
        description: `${selectedUser.name}'s trial has been suspended`,
      });
      setShowSuspendDialog(false);
      setSuspendReason("");
      setAdminNotes("");
      fetchTrialUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to suspend user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivateUser = async (user: TrialUser) => {
    try {
      setIsProcessing(true);
      await trialManagementService.reactivateTrialUser(user.id);
      toast({
        title: "User Reactivated",
        description: `${user.name}'s trial has been reactivated`,
      });
      fetchTrialUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to reactivate user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const openDetailsDialog = (user: TrialUser) => {
    setSelectedUser(user);
    onUserSelect?.(user);
    setShowDetailsDialog(true);
  };

  const getStatusBadge = (status: string, daysRemaining: number) => {
    if (status === "trial_active") {
      if (daysRemaining > 7) {
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      } else if (daysRemaining > 0) {
        return <Badge className="bg-yellow-100 text-yellow-800">Expiring Soon</Badge>;
      } else {
        return <Badge className="bg-red-100 text-red-800">Expired</Badge>;
      }
    } else if (status === "trial_expired_locked") {
      return <Badge className="bg-red-100 text-red-800">Locked</Badge>;
    } else if (status === "active") {
      return <Badge className="bg-blue-100 text-blue-800">Paid</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">{humanizeStatus(status)}</Badge>;
  };

  const getTrialTypeBadge = (type: string) => {
    return (
      <Badge variant="outline" className={type === 'premium' ? 'border-purple-200 text-purple-800' : 'border-blue-200 text-blue-800'}>
        {type.charAt(0).toUpperCase() + type.slice(1)} Trial
      </Badge>
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || user.accountStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600">Error: {error}</p>
        <Button onClick={fetchTrialUsers} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="trial_active">Trial Active</SelectItem>
            <SelectItem value="trial_expired_locked">Trial Expired</SelectItem>
            <SelectItem value="active">Paid Subscription</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12 px-6">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No trial users found</h3>
            <p className="text-muted-foreground">
              {searchTerm || statusFilter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "There are no users currently on trial"}
            </p>
          </div>
        ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Trial Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Trial Period</TableHead>
              <TableHead>Days Remaining</TableHead>
              <TableHead>Last Login</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} className="hover:bg-gray-50">
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    {user.agency && (
                      <div className="text-sm text-blue-600">{user.agency.name}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {getTrialTypeBadge(user.trialType)}
                </TableCell>
                <TableCell>
                  {getStatusBadge(user.accountStatus, user.daysRemaining)}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{format(new Date(user.trialStartDate), 'MMM dd, yyyy')}</div>
                    <div className="text-gray-500">to {format(new Date(user.trialEndDate), 'MMM dd, yyyy')}</div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={`text-sm font-medium ${user.daysRemaining < 0 ? 'text-red-600' : user.daysRemaining < 7 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {user.daysRemaining > 0 ? `${user.daysRemaining} days` : user.daysRemaining === 0 ? 'Expired today' : `${Math.abs(user.daysRemaining)} days ago`}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-500">
                    {user.lastLoginAt ? format(new Date(user.lastLoginAt), 'MMM dd, yyyy') : 'Never'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="View Details & Actions"
                      onClick={() => openDetailsDialog(user)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {(user.accountStatus === 'trial_active' || user.accountStatus === 'trial_expired_locked') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Extend Trial"
                        onClick={() => {
                          setSelectedUser(user);
                          setShowExtendDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        )}
      </div>

      {/* User Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Trial User Details
            </DialogTitle>
            <DialogDescription>
              Manage trial settings for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-6">
              {/* User Information Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">User Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{selectedUser.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{selectedUser.email}</span>
                    </div>
                    {selectedUser.contactPhone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{selectedUser.contactPhone}</span>
                      </div>
                    )}
                    {selectedUser.npn && (
                      <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">NPN: {selectedUser.npn}</span>
                      </div>
                    )}
                    {selectedUser.agency && (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{selectedUser.agency.name}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">User Type:</span>
                      <Badge variant="outline">{selectedUser.userType}</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Trial Information</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Trial Type:</span>
                      {getTrialTypeBadge(selectedUser.trialType)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Status:</span>
                      {getStatusBadge(selectedUser.accountStatus, selectedUser.daysRemaining)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Days Remaining:</span>
                      <span className={`font-medium ${selectedUser.daysRemaining < 0 ? 'text-red-600' : selectedUser.daysRemaining <= 7 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {selectedUser.daysRemaining > 0 ? `${selectedUser.daysRemaining} days` : selectedUser.daysRemaining === 0 ? 'Expires today' : `Expired ${Math.abs(selectedUser.daysRemaining)} days ago`}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Trial Period:</span>
                      <span className="text-sm">
                        {format(new Date(selectedUser.trialStartDate), 'MMM dd, yyyy')} - {format(new Date(selectedUser.trialEndDate), 'MMM dd, yyyy')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Last Login:</span>
                      <span className="text-sm text-gray-600">
                        {selectedUser.lastLoginAt ? format(new Date(selectedUser.lastLoginAt), 'MMM dd, yyyy HH:mm') : 'Never'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Section */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-2">
                  {(selectedUser.accountStatus === 'trial_active' || selectedUser.accountStatus === 'trial_expired_locked') && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowDetailsDialog(false);
                          setShowExtendDialog(true);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Extend Trial
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowDetailsDialog(false);
                          setShowSendReminderDialog(true);
                        }}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Send Reminder
                      </Button>
                    </>
                  )}
                  {selectedUser.accountStatus === 'trial_active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setShowDetailsDialog(false);
                        setShowSuspendDialog(true);
                      }}
                    >
                      <Ban className="w-4 h-4 mr-2" />
                      Suspend
                    </Button>
                  )}
                  {selectedUser.accountStatus === 'trial_expired_locked' && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleReactivateUser(selectedUser)}
                      disabled={isProcessing}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {isProcessing ? 'Reactivating...' : 'Reactivate'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Extend Trial Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Extend Trial</DialogTitle>
            <DialogDescription>
              Extend the trial period for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="extendDays">Extension Days</Label>
              <Input
                id="extendDays"
                type="number"
                min="1"
                max="30"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 7)}
                className="mt-1"
              />
              <p className="text-sm text-gray-500 mt-1">
                Extend trial by 1-30 days
              </p>
            </div>

            <div>
              <Label htmlFor="adminNotes">Admin Notes (Optional)</Label>
              <Textarea
                id="adminNotes"
                placeholder="Add notes about why the trial is being extended..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowExtendDialog(false);
                  setAdminNotes("");
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExtendTrial}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? <LoadingSpinner size="sm" /> : 'Extend Trial'}
              </Button>
            </div>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={showSendReminderDialog} onOpenChange={setShowSendReminderDialog}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Send Trial Reminder</DialogTitle>
            <DialogDescription>
              Send a trial expiration reminder email to {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                This will send an email to <strong>{selectedUser?.email}</strong> reminding them that their trial expires in <strong>{selectedUser?.daysRemaining} days</strong>.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowSendReminderDialog(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendReminder}
                disabled={isProcessing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isProcessing ? <LoadingSpinner size="sm" /> : 'Send Reminder'}
              </Button>
            </div>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Suspend User Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Suspend Trial User</DialogTitle>
            <DialogDescription>
              Suspend the trial account for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                This will immediately lock {selectedUser?.name}'s account. They will no longer be able to access the platform until reactivated.
              </p>
            </div>

            <div>
              <Label htmlFor="suspendReason">Reason for Suspension *</Label>
              <Select value={suspendReason} onValueChange={setSuspendReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="policy_violation">Policy Violation</SelectItem>
                  <SelectItem value="suspicious_activity">Suspicious Activity</SelectItem>
                  <SelectItem value="user_request">User Request</SelectItem>
                  <SelectItem value="payment_issue">Payment Issue</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="suspendNotes">Admin Notes (Optional)</Label>
              <Textarea
                id="suspendNotes"
                placeholder="Add additional details about the suspension..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="mt-1"
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSuspendDialog(false);
                  setSuspendReason("");
                  setAdminNotes("");
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSuspendUser}
                disabled={isProcessing || !suspendReason}
                variant="destructive"
              >
                {isProcessing ? <LoadingSpinner size="sm" /> : 'Suspend User'}
              </Button>
            </div>
          </div>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}
